import type { Draft } from 'immer';
import { followTJoints } from './wallFollow.js';
import {
  add,
  distance,
  EPS,
  localToWorld,
  normalize,
  polygonCentroid,
  scale,
  sub,
  type Vec2,
} from '@indusketch/geometry';
import {
  DEFAULT_DOCK,
  DOCK_GATE_SPACING,
  isDoorType,
  openingPreset,
  zoneCategory,
} from '../catalog.js';
import { buildingCorners, ownedWallId, type RectPlacement } from '../building.js';
import {
  createRoomWalls,
  DEFAULT_INTERIOR_WALL_THICKNESS,
  DEFAULT_ROOM_COLOR,
  DEFAULT_WALL_COLOR,
  duplicatesWall,
  findBuildingAt,
  findBuildingForPolygon,
  roomTypeInfo,
  roomWallGeometry,
  pointsConnected,
  roomPolygon,
  roomRect,
  type RoomRect,
} from '../interior.js';
import {
  clampOpeningOffset,
  freeOpeningOffset,
  outwardSwing,
  sameHeightOpenings,
} from '../derived/walls.js';
import { dockGroup, dockOf } from '../derived/dock.js';
import { wallBase, wallMezzanine } from '../office.js';
import { CommandError } from './history.js';
import { commandText, errorText } from '../texts.js';
import { formatNumber } from '../units.js';
import type {
  Dock,
  Opening,
  OpeningType,
  ProjectDocument,
  Room,
  RoomType,
  Wall,
  WallCladding,
  WallKind,
  Zone,
  ZoneCategory,
} from '../types.js';
import type { Command } from './history.js';

type DraftDoc = Draft<ProjectDocument>;

/* ------------------------------------------------------------------ walls */

export interface NewWallInput {
  readonly id: string;
  readonly start: Vec2;
  readonly end: Vec2;
  readonly height: number;
  readonly thickness?: number;
  readonly kind?: WallKind;
  readonly cladding?: WallCladding;
  readonly color?: string;
  readonly level?: number;
}

export function createWall(input: NewWallInput): Command {
  return {
    label: commandText('drawWall'),
    apply(draft) {
      draft.walls[input.id] = {
        id: input.id,
        start: input.start,
        end: input.end,
        thickness: input.thickness ?? DEFAULT_INTERIOR_WALL_THICKNESS,
        height: input.height,
        baseElevation: 0,
        level: input.level ?? 0,
        kind: input.kind ?? 'interior',
        cladding: input.cladding ?? 'plain',
        ownerId: null,
        color: input.color ?? DEFAULT_WALL_COLOR,
        locked: false,
      };
    },
  };
}

function requireFreeWall(draft: DraftDoc, id: string): Draft<Wall> {
  const wall = draft.walls[id];
  if (!wall) throw new Error(`Wand ${id} existiert nicht`);
  if (wall.ownerId !== null) throw new CommandError(errorText('ownedWall'));
  return wall;
}

/**
 * Moves one endpoint and drags along every endpoint of other free walls that was connected
 * to it, so T- and L-junctions of interior walls stay closed.
 */
function moveEndpoint(
  draft: DraftDoc,
  wall: Draft<Wall>,
  which: 'start' | 'end',
  to: Vec2,
  exclude: Set<string>,
) {
  const from = { x: wall[which].x, y: wall[which].y };
  for (const other of Object.values(draft.walls)) {
    if (exclude.has(other.id) || other.ownerId !== null) continue;
    if (pointsConnected(other.start, from)) other.start = { ...to };
    if (pointsConnected(other.end, from)) other.end = { ...to };
  }
  wall[which] = { ...to };
}

export function translateWall(id: string, delta: Vec2): Command {
  return {
    label: commandText('moveWall'),
    apply(draft) {
      const wall = requireFreeWall(draft, id);
      const before = {
        id,
        start: { ...wall.start },
        end: { ...wall.end },
        thickness: wall.thickness,
        level: wall.level,
      };
      const exclude = new Set([id]);
      moveEndpoint(draft, wall, 'start', add(wall.start, delta), exclude);
      moveEndpoint(draft, wall, 'end', add(wall.end, delta), exclude);
      // Walls meeting it in a T stay against it.
      followTJoints(draft, [before], delta);
    },
  };
}

export function setWallEndpoints(
  id: string,
  start: Vec2,
  end: Vec2,
  label = commandText('updateWall'),
): Command {
  return {
    label,
    apply(draft) {
      const wall = requireFreeWall(draft, id);
      const exclude = new Set([id]);
      moveEndpoint(draft, wall, 'start', start, exclude);
      moveEndpoint(draft, wall, 'end', end, exclude);
    },
  };
}

/** Changes the length by moving the end point along the current direction. */
export function setWallLength(id: string, length: number): Command {
  return {
    label: commandText('wallLength'),
    apply(draft) {
      const wall = requireFreeWall(draft, id);
      if (distance(wall.start, wall.end) < EPS) return;
      const end = add(wall.start, scale(normalize(sub(wall.end, wall.start)), length));
      moveEndpoint(draft, wall, 'end', end, new Set([id]));
    },
  };
}

export function updateWallProps(
  id: string,
  patch: Partial<Pick<Wall, 'height' | 'thickness' | 'kind' | 'cladding' | 'color'>>,
): Command {
  return {
    label: commandText('updateWall'),
    apply(draft) {
      Object.assign(requireFreeWall(draft, id), patch);
    },
  };
}

/** Sets the drywall linings of any wall, also of hall and office walls; null removes them. */
export function setWallLinings(id: string, linings: Wall['linings'] | null): Command {
  return {
    label: commandText('updateWall'),
    apply(draft) {
      const wall = draft.walls[id];
      if (!wall) throw new Error(`Wand ${id} existiert nicht`);
      if (linings && linings.length > 0) wall.linings = linings.map((l) => ({ ...l }));
      else delete wall.linings;
    },
  };
}

/**
 * A free wall on a mezzanine either stands on it or reaches through down to the hall floor;
 * its top stays where it is.
 */
export function setWallThroughMezzanine(id: string, through: boolean): Command {
  return {
    label: commandText('updateWall'),
    apply(draft) {
      const wall = requireFreeWall(draft, id);
      const m = wallMezzanine(draft, wall);
      if (!m) return;
      const top = wallBase(draft, wall) + wall.height;
      wall.level = through ? 0 : 1;
      wall.baseElevation = 0;
      wall.height = Math.max(0.1, through ? top : top - m.elevation);
    },
  };
}

/* ------------------------------------------------------------------ rooms */

export interface NewRoomInput extends RoomRect {
  readonly id: string;
  readonly name: string;
  readonly height: number;
  readonly color?: string;
  readonly wallThickness?: number;
  readonly wallCladding?: WallCladding;
  readonly level?: number;
  readonly roomType?: RoomType;
  /** Turn about `origin` (e.g. parallel to a skewed building); width runs along it. */
  readonly rotation?: number;
}

/** Walls a room on `level` can lean on: the building shells and walls on the same floor. */
function neighbourWalls(draft: DraftDoc, roomId: string, level: number): Wall[] {
  return Object.values(draft.walls).filter(
    (w) =>
      w.ownerId !== roomId &&
      (w.level === level || (w.ownerId !== null && draft.buildings[w.ownerId] !== undefined)),
  );
}

export function createRoom(input: NewRoomInput): Command {
  return {
    label: commandText('createRoom'),
    apply(draft) {
      const polygon = turnedRoomPolygon(input.origin, input.width, input.length, input.rotation ?? 0);
      const roomType = input.roomType ?? 'other';
      const room: Room = {
        id: input.id,
        name: input.name,
        roomType,
        polygon,
        height: input.height,
        color: input.color ?? (input.roomType ? roomTypeInfo(roomType).color : DEFAULT_ROOM_COLOR),
        note: '',
        buildingId: findBuildingForPolygon(draft, polygon),
        level: input.level ?? 0,
        locked: false,
      };
      draft.rooms[room.id] = room;
      syncRoomWalls(
        draft,
        room.id,
        'full',
        input.wallThickness ?? DEFAULT_INTERIOR_WALL_THICKNESS,
        input.wallCladding,
      );
      syncNeighbourRooms(draft, room.id);
    },
  };
}

/**
 * Decides per side whether a room needs its own wall: not where an existing wall (neighbour
 * room, building shell) already stands. `full` also removes walls that became duplicates;
 * `addOnly` only fills gaps, e.g. for neighbours of a room that moved away. Walls carrying
 * doors or windows are never removed. Afterwards the wall geometry is re-derived, so corners
 * next to shared sides close.
 */
function syncRoomWalls(
  draft: DraftDoc,
  id: string,
  mode: 'full' | 'addOnly',
  thicknessHint?: number,
  claddingHint?: WallCladding,
): void {
  const room = draft.rooms[id];
  const frame = room ? roomFrame(room) : null;
  if (!room || !frame) return;
  const { rect, toWorld } = frame;
  const own = [0, 1, 2, 3].map((i) => draft.walls[ownedWallId(id, i)]);
  const first = own.find((w) => w);
  const thickness = first?.thickness ?? thicknessHint ?? DEFAULT_INTERIOR_WALL_THICKNESS;
  const existing = neighbourWalls(draft, id, room.level);
  const full = createRoomWalls(room, rect, thickness, first?.cladding ?? claddingHint).map(
    (w) => ({ ...w, start: toWorld(w.start), end: toWorld(w.end) }),
  );
  const hasOpenings = (wallId: string) =>
    Object.values(draft.openings).some((o) => o.wallId === wallId);
  full.forEach((w, i) => {
    const current = own[i];
    const needed = !duplicatesWall(existing, w);
    if (!current && needed) draft.walls[w.id] = w;
    else if (current && !needed && mode === 'full' && !hasOpenings(current.id))
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- keyed entity maps
      delete draft.walls[current.id];
  });
  const present = [0, 1, 2, 3].map((i) => draft.walls[ownedWallId(id, i)] !== undefined);
  roomWallGeometry(rect, thickness, present).forEach((g, i) => {
    const wall = draft.walls[ownedWallId(id, i)];
    if (!wall) return;
    wall.start = toWorld(g.start);
    wall.end = toWorld(g.end);
    wall.height = room.height;
    wall.level = room.level;
  });
}

/** Rooms on the same floor fill gaps that a changed room left behind. */
function syncNeighbourRooms(draft: DraftDoc, id: string): void {
  const room = draft.rooms[id];
  for (const other of Object.values(draft.rooms)) {
    if (other.id === id || (room && other.level !== room.level)) continue;
    syncRoomWalls(draft, other.id, 'addOnly');
  }
}

/** After rooms were deleted: the remaining rooms get walls where they leaned on them. */
export function fillRoomWallGaps(draft: DraftDoc): void {
  for (const r of Object.values(draft.rooms)) syncRoomWalls(draft, r.id, 'addOnly');
}

/**
 * Recalculates a room's walls: shares walls with neighbours and the building shell, adds
 * walls where nothing stands (e.g. after the neighbour was deleted) and closes the corners.
 */
export function restoreRoomWalls(id: string): Command {
  return {
    label: commandText('recalcRoomWalls'),
    apply(draft) {
      const room = draft.rooms[id];
      if (!room || !roomFrame(room)) throw new CommandError(errorText('rectRoomsOnly'));
      syncRoomWalls(draft, id, 'full');
    },
  };
}

export type RoomPatch = Partial<
  RoomRect & {
    name: string;
    height: number;
    color: string;
    note: string;
    roomType: RoomType;
    /** Turn of the room (its first side against the plan x axis), about its middle. */
    rotation: number;
  }
>;

/**
 * Corner, size and turn of a rectangular room: `origin` is its first corner (the lower left
 * one of a straight room), `width` runs from there along the turn, `length` across it.
 */
export function roomShape(
  room: Pick<Room, 'polygon'>,
): { origin: Vec2; width: number; length: number; rotation: number } | null {
  const straight = roomRect(room);
  if (straight) return { ...straight, rotation: 0 };
  const box = roomBox(room);
  const first = room.polygon[0];
  return box && first
    ? { origin: { ...first }, width: box.width, length: box.depth, rotation: box.rotation }
    : null;
}

/** Outline of a rectangle with its first corner at `origin`, turned by `rotation` about it. */
export function turnedRoomPolygon(
  origin: Vec2,
  width: number,
  length: number,
  rotation: number,
): Vec2[] {
  if (Math.abs(rotation) < 1e-9) return roomPolygon({ origin, width, length });
  const u = { x: Math.cos(rotation), y: Math.sin(rotation) };
  const v = { x: -u.y, y: u.x };
  return [
    origin,
    add(origin, scale(u, width)),
    add(add(origin, scale(u, width)), scale(v, length)),
    add(origin, scale(v, length)),
  ].map((p) => ({ x: p.x, y: p.y }));
}

/** Resizes/moves a room and re-derives its walls, keeping wall ids (and thus openings). */
/** Gives a room a new outline (e.g. following a moved office side) and re-derives its walls. */
export function reshapeRoom(draft: DraftDoc, id: string, polygon: Vec2[]): void {
  const room = draft.rooms[id];
  if (!room) return;
  room.polygon = polygon;
  syncRoomWalls(draft, id, 'full');
  syncNeighbourRooms(draft, id);
}

export function updateRoom(
  id: string,
  patch: RoomPatch,
  label = commandText('updateRoom'),
): Command {
  return {
    label,
    apply(draft) {
      const room = draft.rooms[id];
      if (!room) throw new Error(`Raum ${id} existiert nicht`);
      const current = roomShape(room);
      if (!current) throw new CommandError(errorText('rectRoomsOnly'));
      const width = patch.width ?? current.width;
      const length = patch.length ?? current.length;
      const rotation = patch.rotation ?? current.rotation;
      let origin = patch.origin ?? current.origin;
      if (patch.rotation !== undefined && patch.origin === undefined) {
        // A new turn keeps the room's middle in place.
        const turn = (r: number) => ({
          u: { x: Math.cos(r), y: Math.sin(r) },
          v: { x: -Math.sin(r), y: Math.cos(r) },
        });
        const was = turn(current.rotation);
        const mid = add(
          add(current.origin, scale(was.u, current.width / 2)),
          scale(was.v, current.length / 2),
        );
        const now = turn(rotation);
        origin = sub(sub(mid, scale(now.u, width / 2)), scale(now.v, length / 2));
      }
      if (patch.name !== undefined) room.name = patch.name;
      if (patch.height !== undefined) room.height = patch.height;
      if (patch.color !== undefined) room.color = patch.color;
      if (patch.note !== undefined) room.note = patch.note;
      if (patch.roomType !== undefined) room.roomType = patch.roomType;
      room.polygon = turnedRoomPolygon(origin, width, length, rotation);
      room.buildingId = findBuildingForPolygon(draft, room.polygon);
      syncRoomWalls(draft, id, 'full');
      syncNeighbourRooms(draft, id);
    },
  };
}

/**
 * Rectangle and frame the walls of a room are laid out in: the plan itself for a straight room
 * (sides south, east, north, west as always), the room's own frame for a turned one.
 */
function roomFrame(
  room: Pick<Room, 'polygon'>,
): { rect: RoomRect; toWorld: (p: Vec2) => Vec2 } | null {
  const straight = roomRect(room);
  if (straight) return { rect: straight, toWorld: (p) => p };
  const box = roomBox(room);
  if (!box) return null;
  return {
    rect: { origin: { x: -box.width / 2, y: -box.depth / 2 }, width: box.width, length: box.depth },
    toWorld: (p) => localToWorld(p, { position: box.center, rotation: box.rotation }),
  };
}

/** Turned rectangular room: its own frame (first edge = local x) and size. */
export function roomBox(room: Pick<Room, 'polygon'>): RectPlacement | null {
  const [p0, p1, p2, p3] = room.polygon;
  if (room.polygon.length !== 4 || !p0 || !p1 || !p2 || !p3) return null;
  const width = distance(p0, p1);
  const depth = distance(p1, p2);
  const ux = { x: (p1.x - p0.x) / width, y: (p1.y - p0.y) / width };
  // A rectangle: the second edge is the first turned by 90° and opposite sides are equal.
  const turned = { x: -ux.y * depth, y: ux.x * depth };
  if (distance(add(p1, turned), p2) > 1e-6 || distance(add(p0, turned), p3) > 1e-6) return null;
  return {
    center: { x: (p0.x + p2.x) / 2, y: (p0.y + p2.y) / 2 },
    width,
    depth,
    rotation: Math.atan2(ux.y, ux.x),
  };
}

/** Resizes a turned room to `box` (same turn); its walls follow, keeping their ids. */
export function resizeTurnedRoom(id: string, box: RectPlacement): Command {
  return {
    label: commandText('resizeRoom'),
    apply(draft) {
      const room = draft.rooms[id];
      if (!room) throw new Error(`Raum ${id} existiert nicht`);
      const toWorld = (p: Vec2) =>
        localToWorld(p, { position: box.center, rotation: box.rotation });
      const local: RoomRect = {
        origin: { x: -box.width / 2, y: -box.depth / 2 },
        width: box.width,
        length: box.depth,
      };
      room.polygon = roomPolygon(local).map(toWorld);
      const own = [0, 1, 2, 3].map((i) => draft.walls[ownedWallId(id, i)]);
      const thickness = own.find((w) => w)?.thickness ?? DEFAULT_INTERIOR_WALL_THICKNESS;
      roomWallGeometry(
        local,
        thickness,
        own.map((w) => w !== undefined),
      ).forEach((g, i) => {
        const wall = own[i];
        if (!wall) return;
        wall.start = toWorld(g.start);
        wall.end = toWorld(g.end);
      });
      room.buildingId = findBuildingForPolygon(draft, room.polygon);
    },
  };
}

/* ------------------------------------------------------------------ zones */

export interface NewZoneInput {
  readonly id: string;
  readonly category: ZoneCategory;
  readonly polygon: readonly Vec2[];
  readonly name?: string;
  readonly color?: string;
  readonly level?: number;
}

export function createZone(input: NewZoneInput): Command {
  const info = zoneCategory(input.category);
  return {
    label: commandText('createZone'),
    apply(draft) {
      const zone: Zone = {
        id: input.id,
        name: input.name ?? info.label,
        category: input.category,
        color: input.color ?? info.color,
        polygon: input.polygon.map((p) => ({ ...p })),
        height: null,
        note: '',
        buildingId: findBuildingForPolygon(draft, input.polygon),
        level: input.level ?? 0,
        locked: false,
      };
      draft.zones[zone.id] = zone;
    },
  };
}

export type ZonePatch = Partial<Omit<Zone, 'id' | 'buildingId'>>;

export function updateZone(
  id: string,
  patch: ZonePatch,
  label = commandText('updateZone'),
): Command {
  return {
    label,
    apply(draft) {
      const zone = draft.zones[id];
      if (!zone) throw new Error(`Zone ${id} existiert nicht`);
      Object.assign(zone, patch);
      zone.buildingId = findBuildingForPolygon(draft, zone.polygon);
    },
  };
}

export function translateZone(id: string, delta: Vec2): Command {
  return {
    label: commandText('moveZone'),
    apply(draft) {
      const zone = draft.zones[id];
      if (!zone) throw new Error(`Zone ${id} existiert nicht`);
      zone.polygon = zone.polygon.map((p) => add(p, delta));
      zone.buildingId = findBuildingAt(draft, polygonCentroid(zone.polygon));
    },
  };
}

/* --------------------------------------------------------------- openings */

const dockDefaults = (type: OpeningType): { dock?: Dock } =>
  type === 'loading-dock' ? { dock: { ...DEFAULT_DOCK } } : {};

/**
 * New doors in the shell of a hall or office and in room walls open outwards (escape
 * direction); doors in free walls keep the plain default.
 */
export function outwardDoorSwing(
  doc: Pick<ProjectDocument, 'buildings' | 'rooms'>,
  wall: Pick<Wall, 'start' | 'end' | 'thickness' | 'ownerId'>,
  type: OpeningType,
): { swing?: Opening['swing'] } {
  if (!isDoorType(type)) return {};
  const owner = wall.ownerId;
  const building = owner ? doc.buildings[owner] : undefined;
  const inside = building
    ? buildingCorners(building)
    : owner
      ? (doc.rooms[owner]?.polygon ?? null)
      : null;
  const swing = outwardSwing(wall, inside);
  return swing ? { swing } : {};
}

export interface NewOpeningInput {
  readonly id: string;
  readonly wallId: string;
  readonly type: OpeningType;
  /** Defaults to centered on the wall. */
  readonly offsetFromWallStart?: number;
  readonly width?: number;
  readonly height?: number;
  readonly sillHeight?: number;
  /** Loading docks only; defaults to `DEFAULT_DOCK`. */
  readonly dock?: Dock;
}

export function createOpening(input: NewOpeningInput): Command {
  const preset = openingPreset(input.type);
  return {
    label: commandText('insertOpening', { type: preset.label }),
    apply(draft) {
      const wall = draft.walls[input.wallId];
      if (!wall) throw new Error(`Wand ${input.wallId} existiert nicht`);
      const width = input.width ?? preset.width;
      const length = distance(wall.start, wall.end);
      const sillHeight = input.sillHeight ?? preset.sillHeight;
      const height = input.height ?? Math.min(preset.height, wall.height - sillHeight);
      const others = sameHeightOpenings(
        Object.values(draft.openings).filter((o) => o.wallId === wall.id),
        sillHeight,
        height,
      );
      const preferred = clampOpeningOffset(
        wall,
        width,
        input.offsetFromWallStart ?? (length - width) / 2,
      );
      const offset = freeOpeningOffset(wall, others, width, preferred);
      if (offset === null)
        throw new CommandError(
          errorText('noSpace', { type: preset.label, width: formatNumber(width) }),
        );
      const opening: Opening = {
        id: input.id,
        wallId: input.wallId,
        type: input.type,
        offsetFromWallStart: offset,
        width,
        height,
        sillHeight,
        ...(input.dock && input.type === 'loading-dock'
          ? { dock: { ...input.dock } }
          : dockDefaults(input.type)),
        ...outwardDoorSwing(draft, wall, input.type),
        locked: false,
      };
      draft.openings[opening.id] = opening;
    },
  };
}

export interface OpeningRowInput {
  readonly ids: readonly string[];
  readonly wallId: string;
  readonly type: OpeningType;
  /** Clear distance between neighbouring openings. */
  readonly gap: number;
  readonly width?: number;
  readonly height?: number;
  readonly sillHeight?: number;
  /** Start of the row; defaults to a row centered on the wall. */
  readonly offsetFromWallStart?: number;
  /** Loading docks only: one entry per opening, else `DEFAULT_DOCK` for each. */
  readonly docks?: readonly Dock[];
  readonly label?: string;
}

/** Several equal openings in one step, e.g. a row of loading docks with 3 m spacing. */
export function createOpeningRow(input: OpeningRowInput): Command {
  const preset = openingPreset(input.type);
  return {
    label:
      input.label ??
      commandText('insertOpening', { type: `${String(input.ids.length)} × ${preset.label}` }),
    apply(draft) {
      const wall = draft.walls[input.wallId];
      if (!wall) throw new Error(`Wand ${input.wallId} existiert nicht`);
      const width = input.width ?? preset.width;
      const n = input.ids.length;
      const total = n * width + (n - 1) * input.gap;
      const length = distance(wall.start, wall.end);
      if (total > length + EPS)
        throw new CommandError(
          errorText('rowTooLong', { n, type: preset.label, total: formatNumber(total, 2, 2) }),
        );
      const start = input.offsetFromWallStart ?? (length - total) / 2;
      input.ids.forEach((id, i) => {
        const others = Object.values(draft.openings).filter((o) => o.wallId === wall.id);
        const wanted = start + i * (width + input.gap);
        const offset = freeOpeningOffset(wall, others, width, wanted);
        if (offset === null || Math.abs(offset - wanted) > EPS) {
          throw new CommandError(errorText('rowOverlap'));
        }
        const sillHeight = input.sillHeight ?? preset.sillHeight;
        draft.openings[id] = {
          id,
          wallId: wall.id,
          type: input.type,
          offsetFromWallStart: offset,
          width,
          height: input.height ?? Math.min(preset.height, wall.height - sillHeight),
          sillHeight,
          ...(input.docks?.[i] && input.type === 'loading-dock'
            ? { dock: { ...input.docks[i] } }
            : dockDefaults(input.type)),
          ...outwardDoorSwing(draft, wall, input.type),
          locked: false,
        };
      });
    },
  };
}

export interface DoubleDockInput {
  readonly ids: readonly [string, string];
  readonly wallId: string;
  /** Start of the first gate; defaults to the pair centered on the wall. */
  readonly offsetFromWallStart?: number;
}

/**
 * Two loading docks side by side (4 m center to center) sharing one depressed approach that is
 * twice as wide and centered between the gates; each gate keeps its own leveler.
 */
export function createDoubleDock(input: DoubleDockInput): Command {
  return createDockRow(input);
}

export interface DockRowInput {
  /** One id per gate; the first gate holds the shared approach. */
  readonly ids: readonly string[];
  readonly wallId: string;
  /** Start of the first gate; defaults to the row centered on the wall. */
  readonly offsetFromWallStart?: number;
}

/**
 * Loading docks side by side (4 m center to center) sharing one depressed approach as wide as
 * all of them, with fall protection only on its two outer sides; each gate keeps its leveler.
 */
export function createDockRow(input: DockRowInput): Command {
  const n = input.ids.length;
  const width = openingPreset('loading-dock').width;
  return createOpeningRow({
    ids: input.ids,
    wallId: input.wallId,
    type: 'loading-dock',
    gap: DOCK_GATE_SPACING - width,
    ...(input.offsetFromWallStart !== undefined
      ? { offsetFromWallStart: input.offsetFromWallStart }
      : {}),
    docks: input.ids.map((_, i) =>
      i === 0
        ? {
            ...DEFAULT_DOCK,
            bayWidth: n * DEFAULT_DOCK.bayWidth,
            approachOffset: ((n - 1) * DOCK_GATE_SPACING) / 2,
          }
        : { ...DEFAULT_DOCK, approach: false },
    ),
    label:
      n === 1
        ? commandText('insertOpening', { type: openingPreset('loading-dock').label })
        : commandText('insertDoubleDock'),
  });
}

/** One more gate at the end of a shared trough; the trough grows by one bay. */
export function addDockGate(leadId: string, id: string): Command {
  return {
    label: commandText('insertDoubleDock'),
    apply(draft) {
      const lead = draft.openings[leadId];
      const wall = lead ? draft.walls[lead.wallId] : undefined;
      const d = lead ? dockOf(lead) : null;
      if (!lead || !wall || !d?.approach) throw new Error(`Rampe ${leadId} existiert nicht`);
      const group = dockGroup(wall, Object.values(draft.openings), lead);
      const last = group?.gates[group.gates.length - 1] ?? lead;
      const offset = last.offsetFromWallStart + DOCK_GATE_SPACING;
      const others = Object.values(draft.openings).filter((x) => x.wallId === wall.id);
      if (
        offset + lead.width > distance(wall.start, wall.end) + EPS ||
        freeOpeningOffset(wall, others, lead.width, offset) !== offset
      )
        throw new CommandError(
          errorText('noSpace', {
            type: openingPreset('loading-dock').label,
            width: formatNumber(lead.width),
          }),
        );
      draft.openings[id] = {
        id,
        wallId: wall.id,
        type: 'loading-dock',
        offsetFromWallStart: offset,
        width: lead.width,
        height: lead.height,
        sillHeight: lead.sillHeight,
        dock: { ...d, approach: false, approachOffset: 0 },
        locked: false,
      };
      lead.dock = {
        ...d,
        bayWidth: d.bayWidth + DOCK_GATE_SPACING,
        approachOffset: d.approachOffset + DOCK_GATE_SPACING / 2,
      };
    },
  };
}

/** Removes the last gate of a shared trough; the trough shrinks by one bay. */
export function removeDockGate(leadId: string): Command {
  return {
    label: commandText('remove'),
    apply(draft) {
      const lead = draft.openings[leadId];
      const wall = lead ? draft.walls[lead.wallId] : undefined;
      const d = lead ? dockOf(lead) : null;
      if (!lead || !wall || !d?.approach) throw new Error(`Rampe ${leadId} existiert nicht`);
      const group = dockGroup(wall, Object.values(draft.openings), lead);
      const last = group?.gates.filter((g) => g.id !== lead.id).at(-1);
      if (!last) return;
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- keyed entity maps
      delete draft.openings[last.id];
      const shift = last.offsetFromWallStart - lead.offsetFromWallStart;
      const step = Math.min(DOCK_GATE_SPACING, Math.max(0, shift));
      lead.dock = {
        ...d,
        bayWidth: Math.max(DEFAULT_DOCK.bayWidth, d.bayWidth - step),
        approachOffset: Math.max(0, d.approachOffset - step / 2),
      };
    },
  };
}

/**
 * Switches the type and takes over its standard size (a gate turned into a door becomes
 * 1.0 × 2.1 m), centered where the opening was. Changing to a loading dock adds the depressed
 * approach, changing away removes it.
 */
export function changeOpeningType(id: string, type: OpeningType): Command {
  return {
    label: commandText('changeOpeningType'),
    apply(draft) {
      const o = draft.openings[id];
      if (!o) throw new Error(`Öffnung ${id} existiert nicht`);
      const wall = draft.walls[o.wallId];
      const preset = openingPreset(type);
      if (wall && o.type !== type) {
        const center = o.offsetFromWallStart + o.width / 2;
        const width = Math.min(preset.width, distance(wall.start, wall.end));
        o.width = width;
        o.sillHeight = Math.min(preset.sillHeight, Math.max(0, wall.height - 0.3));
        o.height = Math.min(preset.height, wall.height - o.sillHeight);
        o.offsetFromWallStart = clampOpeningOffset(wall, width, center - width / 2);
      }
      o.type = type;
      if (type === 'loading-dock') {
        o.dock ??= { ...DEFAULT_DOCK };
        o.sillHeight = 0;
      } else {
        delete o.dock;
      }
    },
  };
}

/** Puts several openings at new distances from their wall starts in one step. */
export function placeOpenings(
  offsets: ReadonlyMap<string, number>,
  label = commandText('moveOpening'),
): Command {
  return {
    label,
    apply(draft) {
      for (const [id, offset] of offsets) {
        const o = draft.openings[id];
        if (o) o.offsetFromWallStart = offset;
      }
    },
  };
}

/**
 * Own frame and leaf colours for several openings in one step; `null` returns to the colours
 * of the building, a missing key leaves that colour as it is.
 */
export function setOpeningColors(
  ids: readonly string[],
  colors: { readonly frameColor?: string | null; readonly leafColor?: string | null },
): Command {
  return {
    label: commandText('updateOpening'),
    apply(draft) {
      const { frameColor, leafColor } = colors;
      for (const id of ids) {
        const o = draft.openings[id];
        if (!o) continue;
        if (frameColor === null) delete o.frameColor;
        else if (frameColor !== undefined) o.frameColor = frameColor;
        if (leafColor === null) delete o.leafColor;
        else if (leafColor !== undefined) o.leafColor = leafColor;
      }
    },
  };
}

export function updateOpening(
  id: string,
  patch: Partial<Omit<Opening, 'id' | 'wallId'>>,
  label = commandText('updateOpening'),
): Command {
  return {
    label,
    apply(draft) {
      const o = draft.openings[id];
      if (!o) throw new Error(`Öffnung ${id} existiert nicht`);
      Object.assign(o, patch);
    },
  };
}
